import { Dispatch, SetStateAction, useCallback } from 'react';
import { AlertType } from '../../components';
import { useHandleApiResponse, useToast } from '../../hooks';
import { KnowledgeHubRecord, useUpdateKnowledgeHubMutation } from '../../services';
import { KnowledgeHubRecordType } from '../../types';
import KnowledgeHubField from './KnowledgeHubField';

interface EditKnowledgeHubItemProps {
  currPage: number;
  initialValue: string;
  recordId: string;
  type: KnowledgeHubRecordType;
  value: string;
  exitEditMode: () => void;
  setCurrPage: Dispatch<SetStateAction<number>>;
  setExistingRecords: Dispatch<SetStateAction<KnowledgeHubRecord[]>>;
  setValue: Dispatch<SetStateAction<string>>;
}

const EditKnowledgeHubItem = ({
  currPage,
  initialValue,
  recordId,
  type,
  value,
  exitEditMode,
  setCurrPage,
  setExistingRecords,
  setValue,
}: EditKnowledgeHubItemProps) => {
  const [updateKnowledgeHub, { isLoading: isUpdateLoading }] = useUpdateKnowledgeHubMutation();

  const handleApiResponse = useHandleApiResponse();
  const { showToast } = useToast();

  // Updates the local state with the new updated record.
  // If the user is not on the first page, navigate to the first page, refetching the records.
  // Otherwise, move the updated record to the top of the local state list.
  const onEditSuccess = useCallback(
    (record: KnowledgeHubRecord) => {
      if (currPage > 0) {
        setCurrPage(0);
        setExistingRecords((prev) => prev.filter((r) => r.id !== record.id));
      } else {
        setExistingRecords((prev) => [record, ...prev.filter((r) => r.id !== record.id)]);
      }
      exitEditMode();
    },
    [currPage, setCurrPage, setExistingRecords, exitEditMode]
  );

  const handleSave = useCallback(async () => {
    try {
      const response = await updateKnowledgeHub({ recordId, content: value });
      handleApiResponse({
        response,
        errorMsg: 'Failed to update knowledge hub record. Please try again.',
        onSuccess: (data) => onEditSuccess(data.record),
      });
    } catch (error) {
      console.error(error);
      showToast({ message: 'Something went wrong. Please try again.', type: AlertType.ERROR });
    }
  }, [recordId, value, updateKnowledgeHub, handleApiResponse, onEditSuccess, showToast]);

  // Cancels edit mode by resetting the value and exiting.
  const handleCancel = useCallback(() => {
    setValue(initialValue);
    exitEditMode();
  }, [initialValue, setValue, exitEditMode]);

  return (
    <KnowledgeHubField
      isLoading={isUpdateLoading}
      type={type}
      value={value}
      handleCancel={handleCancel}
      handleSave={handleSave}
      setValue={setValue}
    />
  );
};

export default EditKnowledgeHubItem;
